import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ShoppingCart, Search, ChevronsUpDown, Check } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { useCart } from "@/components/CartProvider";
import { ProductCard, type Product } from "@/components/ProductCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useShopSettings, DEFAULT_SHOP_SETTINGS } from "@/hooks/useShopSettings";

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface ProductsResponse {
  products: Product[];
  nextCursor: string | null;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function Home() {
  const { itemCount } = useCart();
  const [category, setCategory] = useState<string>("all");
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [sizeFilter, setSizeFilter] = useState<string>("all");
  const [sizeOpen, setSizeOpen] = useState(false);
  const [sort, setSort] = useState<string>("newest");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 350);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const { data: settings } = useShopSettings();
  const shopName = settings?.shop_name ?? DEFAULT_SHOP_SETTINGS.shop_name;

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiFetch<Category[]>("/api/categories"),
  });

  const { data: sizes } = useQuery({
    queryKey: ["product-sizes"],
    queryFn: () => apiFetch<string[]>("/api/products/sizes"),
  });

  const showSizeFilter = (sizes?.length ?? 0) > 0;

  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["products", category, sort, search, sizeFilter],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "20", sort });
      if (category && category !== "all") params.set("category", category);
      if (search) params.set("search", search);
      if (sizeFilter && sizeFilter !== "all") params.set("size", sizeFilter);
      if (pageParam) params.set("cursor", pageParam);
      return apiFetch<ProductsResponse>(`/api/products?${params}`);
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const products = data?.pages.flatMap((p) => p.products) ?? [];

  useEffect(() => {
    if (!showSizeFilter && sizeFilter !== "all") setSizeFilter("all");
  }, [showSizeFilter, sizeFilter]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-6xl mx-auto h-14 flex items-center justify-between px-4">
          <Link to="/" aria-label={`${shopName} — Početna stranica`}>
            <span className="text-xl font-semibold tracking-tight text-foreground">
              {shopName}
            </span>
          </Link>

          <Link
            to="/cart"
            className="relative flex items-center gap-1.5 text-sm text-foreground/70 hover:text-foreground transition-colors"
          >
            <ShoppingCart className="size-5" />
            <span className="hidden sm:inline">Korpa</span>
            {itemCount > 0 && (
              <span className="absolute -top-2 -right-2 size-5 bg-foreground text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {itemCount > 99 ? "99+" : itemCount}
              </span>
            )}
          </Link>
        </div>
      </header>

      {/* Hero tagline */}
      <section className="relative overflow-hidden" aria-label="Uvod">
        <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14">
          <h1 className="text-xl sm:text-2xl md:text-[28px] font-light leading-snug tracking-tight text-foreground/85 max-w-xl">
            {settings?.home_subtitle ?? DEFAULT_SHOP_SETTINGS.home_subtitle}
          </h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />
      </section>

      {/* Search + Filters */}
      <search className="bg-white border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row gap-2 sm:items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Pretraži proizvode..."
              aria-label="Pretraži proizvode"
              className="w-full rounded-lg border border-border bg-background pl-9 pr-4 py-2 text-sm h-9 focus:ring-1 focus:ring-foreground focus:border-foreground outline-none"
            />
          </div>

          {/* Dropdowns */}
          <div className={`grid ${showSizeFilter ? "grid-cols-3" : "grid-cols-2"} sm:flex gap-2 sm:shrink-0`}>
            <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
              <PopoverTrigger asChild>
                <button
                  role="combobox"
                  aria-expanded={categoryOpen}
                  className="w-full sm:w-auto sm:min-w-[160px] h-9 rounded-lg border border-border bg-background px-3 text-sm flex items-center justify-between gap-2 hover:bg-accent/50 transition-colors"
                >
                  <span className="truncate">
                    {category === "all"
                      ? "Sve kategorije"
                      : categories?.find((c) => c.slug === category)?.name ?? "Sve kategorije"}
                  </span>
                  <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Traži kategoriju..." className="h-9" />
                  <CommandList>
                    <CommandEmpty>Nema rezultata.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => { setCategory("all"); setCategoryOpen(false); }}
                      >
                        Sve kategorije
                        <Check className={cn("ml-auto size-4", category === "all" ? "opacity-100" : "opacity-0")} />
                      </CommandItem>
                      {categories?.map((cat) => (
                        <CommandItem
                          key={cat.id}
                          value={cat.name}
                          onSelect={() => { setCategory(cat.slug); setCategoryOpen(false); }}
                        >
                          {cat.name}
                          <Check className={cn("ml-auto size-4", category === cat.slug ? "opacity-100" : "opacity-0")} />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {showSizeFilter && (
              <Popover open={sizeOpen} onOpenChange={setSizeOpen}>
                <PopoverTrigger asChild>
                  <button
                    role="combobox"
                    aria-expanded={sizeOpen}
                    className="w-full sm:w-auto sm:min-w-[120px] h-9 rounded-lg border border-border bg-background px-3 text-sm flex items-center justify-between gap-2 hover:bg-accent/50 transition-colors"
                  >
                    <span className="truncate">
                      {sizeFilter === "all" ? "Sve veličine" : sizeFilter}
                    </span>
                    <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[160px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Traži veličinu..." className="h-9" />
                    <CommandList>
                      <CommandEmpty>Nema rezultata.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all"
                          onSelect={() => { setSizeFilter("all"); setSizeOpen(false); }}
                        >
                          Sve veličine
                          <Check className={cn("ml-auto size-4", sizeFilter === "all" ? "opacity-100" : "opacity-0")} />
                        </CommandItem>
                        {sizes?.map((s) => (
                          <CommandItem
                            key={s}
                            value={s}
                            onSelect={() => { setSizeFilter(s); setSizeOpen(false); }}
                          >
                            {s}
                            <Check className={cn("ml-auto size-4", sizeFilter === s ? "opacity-100" : "opacity-0")} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-full sm:w-auto sm:min-w-[140px] h-9 rounded-lg border-border text-sm">
                <SelectValue placeholder="Sortiraj" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Najnovije</SelectItem>
                <SelectItem value="price_asc">Cijena: ↑</SelectItem>
                <SelectItem value="price_desc">Cijena: ↓</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </search>

      {/* Product grid */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <h2 className="sr-only">Proizvodi</h2>
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square rounded-xl bg-muted" />
                <div className="mt-3 space-y-2 px-0.5">
                  <div className="h-3.5 bg-muted rounded-md w-3/4" />
                  <div className="h-3 bg-muted rounded-md w-1/2" />
                  <div className="h-4 bg-muted rounded-md w-1/3 mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-2">
            <p className="text-lg font-medium text-muted-foreground">
              Nema pronađenih proizvoda
            </p>
            <p className="text-sm text-muted-foreground">
              Pokušajte promijeniti filtere
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {products.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        )}

        <div ref={sentinelRef} className="h-1" />

        {isFetchingNextPage && (
          <div className="flex items-center justify-center gap-2 py-6">
            <div className="size-6 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
            <span className="text-sm text-muted-foreground">Učitavanje...</span>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 mt-4">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col items-center gap-3">
          <nav aria-label="Informacije" className="flex items-center gap-3 text-xs text-muted-foreground">
            <Link to="/shipping" className="hover:text-foreground transition-colors">
              Dostava
            </Link>
            <span className="text-border" aria-hidden="true">·</span>
            <Link to="/payment" className="hover:text-foreground transition-colors">
              Plaćanje
            </Link>
          </nav>
          <p className="text-[10px] text-muted-foreground/50 tracking-wider">{shopName}</p>
        </div>
      </footer>
    </div>
  );
}
